/**
 * RTK Query endpoints — File upload
 *
 * Replaces the legacy `requestUpload` + `upload` pair from
 * `src/pages/{users,items}/actions.js`.
 *
 * The S3 signed-URL flow is two-step:
 *   1. POST /su/upload  →  { url, fields }
 *   2. POST <url>      →  multipart/form-data with `fields...` + `file`, no Authorization
 *
 * This is exposed as a single mutation `useUploadFileMutation()` that
 * orchestrates both steps via a custom `queryFn`. Callers pass `{ file, meta }`
 * and get back the upload metadata returned by step 1 (key, etc.) so they can
 * pass it to whichever entity mutation needs it (`useEditUserMutation`, ...).
 */

import { baseApi } from './baseApi';

const uploadApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadFile: builder.mutation({
      // ({ file, meta }) → { uploaded, ...rest from step 1 }
      async queryFn({ file, meta }, api, _extraOptions, fetchWithBQ) {
        // Step 1: request a signed URL using normal auth.
        const step1 = await fetchWithBQ({
          url: '/su/upload',
          method: 'POST',
          body: meta,
        });
        if (step1.error) return { error: step1.error };

        // Backend wraps the upload URL + fields under `formData`. Be lenient
        // so future callers that flatten the shape still work.
        const presigned = step1.data?.formData || step1.data || {};
        const { url, fields = {} } = presigned;
        if (!url) {
          return { error: { status: 'CUSTOM_ERROR', error: 'signed url missing' } };
        }

        // Step 2: post the file to the signed URL. We bypass fetchBaseQuery here
        // because we must NOT send Authorization/X-Tenant-Id to S3.
        const form = new FormData();
        Object.entries(fields).forEach(([k, v]) => form.append(k, v));
        form.append('file', file);

        try {
          const res = await fetch(url, { method: 'POST', body: form });
          if (!res.ok) {
            return {
              error: {
                status: res.status,
                data: await res.text(),
              },
            };
          }
          return { data: { ...step1.data, uploaded: true } };
        } catch (err) {
          return {
            error: { status: 'FETCH_ERROR', error: String(err) },
          };
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const { useUploadFileMutation } = uploadApi;
export default uploadApi;
